import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';

// Models
import { FirebaseSubjectListener } from 'src/app/models/firebase-listener/firebase-listener.model';
import { Conversation, ConversationList, Message } from 'src/app/services/messaging/models';
import { MessagingService } from 'src/app/services/messaging/messaging.service';
import { MuteService } from 'src/app/services/mute/mute.service';
import { UserService } from 'src/app/services/user/user.service';
import { BrowserNotificationService } from '../browser-notification/browser-notification.service';

type LastMessagesReadMap = { [conversationId: Conversation['id']]: Message['id'] };

@Injectable({
  providedIn: 'root'
})
export class MessagingListenerService {

  public conversations: ConversationList;
  public listener: BehaviorSubject<ConversationList>;
  public totalUnreadCount$ = new BehaviorSubject<number>(0);

  private initialized: boolean;
  private lastMessageReadIds: LastMessagesReadMap = {};
  private activeConversationId: string | null = null;
  private messagesListener: FirebaseSubjectListener<Subject<ConversationList>>;
  private notificationSound: HTMLAudioElement;
  constructor(
    private browserNotification: BrowserNotificationService,
    private messaging: MessagingService,
    private muteService: MuteService,
    private router: Router,
    private user: UserService,
  ) {
    this.browserNotification.requestPermission();
  }

  public listen() {
    if (this.listener) {
      return this.listener;
    }

    this.listener = new BehaviorSubject<ConversationList>(null);

    this.messagesListener = this.messaging.listenConversations();
    this.messagesListener.subject.subscribe(async conversations => {
      if (!conversations) {
        return;
      }

      if (!this.conversations) {
        this.lastMessageReadIds = await this.fetchLastMessageReadIds(conversations);
        this.conversations = conversations;
      }

      const oldConversations = [...this.conversations.map(conversation => ({ ...conversation }))];
      if (this.conversations.length > conversations.length) {
        [...Array(this.conversations.length - conversations.length)].forEach(() => this.conversations.pop());
      }

      if (this.conversations.length < conversations.length) {
        const newConversations = conversations.slice(this.conversations.length);
        newConversations.forEach(conv => this.conversations.push(conv));
        // Initialize lastMessageReadIds for newly added conversations so badge counts
        // are computed correctly instead of treating undefined as "all unread"
        const newIds = await this.fetchLastMessageReadIds(newConversations);
        Object.assign(this.lastMessageReadIds, newIds);
      }

      conversations.forEach((conversation, index) => {
        delete conversation.newMessageCount;
        Object.keys(conversation).forEach(key => (this.conversations[index][key] = conversation[key]));
      });

      const promises = conversations.map(async (conversation, index) => {
        // User is currently viewing this conversation — always 0
        if (this.activeConversationId === conversation.id) {
          this.lastMessageReadIds[conversation.id] = conversation.lastMessage?.id;
          return '0';
        }

        const oldConversation = oldConversations?.find(({ id }) => id === conversation.id);
        if (!this.initialized || oldConversation?.lastMessage?.id !== conversation.lastMessage?.id) {
          if (this.initialized && !this.muteService.isMuted(conversation.id)) {
            if (this.shouldShowBrowserNotification(conversation.lastMessage?.sender, this.user.user.uid, conversation.lastMessage?.content)) {
              const senderName = conversation.lastMessage?.senderName || conversation.name;
              const notification = await this.browserNotification.showNotification(senderName, {
                body: conversation.lastMessage?.content,
                icon: '/assets/icon/favicon.png',
              });

              if (notification) {
                notification.onclick = () => {
                  this.router.navigate(['/messaging'], { queryParams: { conversationId: conversation.id } });
                  window.focus();
                };
              }

              this.playNotificationSound();
            }
          }

          if (conversation.lastMessage?.sender !== this.user.user.uid) {
            return await this.getNewMessageCount(conversation, this.lastMessageReadIds[conversation.id]);
          }

          return '0';
        }

        return oldConversation?.newMessageCount || '0';
      });

      this.initialized = true;
      const newMessageCounts = await Promise.all(promises);
      const countMap = new Map(conversations.map((c, i) => [c.id, newMessageCounts[i]]));
      this.conversations.forEach(conversation => {
        if (countMap.has(conversation.id)) conversation.newMessageCount = countMap.get(conversation.id);
      });
      this.listener.next(this.conversations);
      // Update tab title count from DM conversations (channel/AI counts are added by ConversationListComponent)
      this.updateTotalUnreadCount(this.conversations);
      // this.updateConversationOnline(true);
      // this.interval.setInterval(this.converstionOnlineInterval, () => this.updateConversationOnline(true), 1 * 60 * 1000);
    });

    return this.listener;
  }

  private shouldShowBrowserNotification(sender: string, user: string, lastMessage: string): boolean {
    return sender !== user && !!lastMessage && document.visibilityState !== 'visible';
  }

  public async getNewMessageCount(conversation: Partial<Conversation>, lastMessageId: string): Promise<string> {
    const messageCount = await this.getMessagesCountFromMessageId(conversation.id, lastMessageId);
    if (messageCount > 9) {
      return '9+';
    }

    return messageCount.toString();
  }

  public async fetchLastMessageReadIds(conversations: ConversationList): Promise<LastMessagesReadMap> {

    const promises = conversations.map(async conversation => ({
        conversationId: conversation.id,
        lastReadMessageId: await this.messaging.fetchLastReadMessageId(conversation.id, this.user.user.uid),
    }));

    const lastMessageReadIdsArray = await Promise.all(promises);
    const lastMessageReadIds: LastMessagesReadMap = lastMessageReadIdsArray.reduce(
      (acc, { conversationId, lastReadMessageId }) => ({ ...acc, [conversationId]: lastReadMessageId }), {}
    );

    return lastMessageReadIds;
  }

  public updateCachedLastMessageRead(conversationId: Conversation['id'], messageId: Message['id']) {
    this.lastMessageReadIds[conversationId] = messageId;
  }

  public setActiveConversation(conversationId: string | null): void {
    this.activeConversationId = conversationId;
  }

  public updateTotalUnreadCount(allConversations: ConversationList): void {
    const mutedIds = this.muteService.mutedIds$.getValue();
    let total = 0;
    for (const c of allConversations) {
      if (mutedIds.has(c.id)) continue;
      const count = c.newMessageCount;
      if (!count || count === '0') continue;
      if (count === '9+') {
        total += 10;
      } else {
        total += parseInt(count, 10) || 0;
      }
    }
    this.totalUnreadCount$.next(total);
  }

  private playNotificationSound(): void {
    if (!this.notificationSound) {
      this.notificationSound = new Audio('/assets/sound/notification.mp3');
    }
    this.notificationSound.currentTime = 0;
    this.notificationSound.play().catch(() => {});
  }

  private async getMessagesCountFromMessageId(conversationId: string, lastMessageId: string): Promise<number> {
    if (!lastMessageId) {
      // No read marker means the user has never opened this conversation.
      // Return 0 to avoid phantom badge counts from the size-based fallback.
      return 0;
    }

    return await this.messaging.getMessagesCountAfter(conversationId, lastMessageId);
  }

}
